/**
 * inbox-derive.ts — Pure derive helpers for inbox Someday/Waiting parking (P2-10).
 * No side effects; all functions deterministic given inputs.
 * TIMEZONE: never use toISOString().slice — use date.ts helpers for local dates.
 */
import type { InboxItem, InboxStatus } from "../types";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InboxGroups {
  inbox: InboxItem[];
  someday: InboxItem[];
  waiting: InboxItem[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Effective status: absent/undefined → "inbox" for back-compat. */
function effectiveStatus(item: InboxItem): InboxStatus {
  return item.status ?? "inbox";
}

/** Parse a local ISO date string into a Date at local midnight (no UTC shift). */
function fromLocal(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

// ── Group by status ───────────────────────────────────────────────────────────

/**
 * Group inbox items by their effective status.
 * Items with undefined/absent status are placed in "inbox" (back-compat).
 */
export function groupInbox(items: InboxItem[]): InboxGroups {
  const inbox: InboxItem[] = [];
  const someday: InboxItem[] = [];
  const waiting: InboxItem[] = [];

  for (const item of items) {
    const status = effectiveStatus(item);
    if (status === "someday") someday.push(item);
    else if (status === "waiting") waiting.push(item);
    else inbox.push(item);
  }

  return { inbox, someday, waiting };
}

// ── Stale detection ───────────────────────────────────────────────────────────

/**
 * Items in "inbox" status older than `days` days from `now`.
 * Only looks at items with effective status "inbox" — parked items are excluded.
 */
export function staleInbox(items: InboxItem[], now: string, days = 3): InboxItem[] {
  const nowDate = fromLocal(now);
  const threshold = nowDate.getTime() - days * 24 * 60 * 60 * 1000;

  return items.filter((item) => {
    if (effectiveStatus(item) !== "inbox") return false;
    const created = new Date(item.createdAt).getTime();
    return created <= threshold;
  });
}

/**
 * Returns true when any inbox-status item is older than `days` days.
 * Used to show the stale nudge banner in inbox-view.
 */
export function isInboxStale(items: InboxItem[], now: string, days = 3): boolean {
  return staleInbox(items, now, days).length > 0;
}

// ── Waiting overdue ───────────────────────────────────────────────────────────

/**
 * Returns true when a waiting item's follow-up date (`waitingUntil`) is
 * on or before `today`. Items without waitingUntil are never overdue.
 */
export function waitingOverdue(item: InboxItem, today: string): boolean {
  if (effectiveStatus(item) !== "waiting") return false;
  if (!item.waitingUntil) return false;
  return item.waitingUntil <= today;
}
